import { HEIGHT_PRESETS_CM, LIMITS, PALLET_FOOTPRINTS, type PalletFootprintType } from "./constants";
import { BoundedInt } from "./boundedInt";
import { LdmValue } from "./ldmValue";
import type { CargoLine, CargoSizeMode, PalletType } from "./types";

interface DimensionOption {
  value: string;
  label: string;
}

export class CargoDimensionMapper {
  static hasFootprint(type: PalletType): type is PalletFootprintType {
    return type in PALLET_FOOTPRINTS;
  }

  private formatLabel(type: PalletFootprintType, heightCm: string): string {
    const fp = PALLET_FOOTPRINTS[type];
    return `${fp.length} \u00d7 ${fp.width} \u00d7 ${heightCm}`;
  }

  options(type: PalletType, currentHeight: string): DimensionOption[] {
    if (!CargoDimensionMapper.hasFootprint(type)) return [];
    const heights = new Set<string>(HEIGHT_PRESETS_CM);
    const extra = currentHeight.trim();
    if (extra) heights.add(extra);
    return [...heights]
      .sort((a, b) => Number(a) - Number(b))
      .map((height) => ({
        value: height,
        label: this.formatLabel(type, height),
      }));
  }

  applyType(line: CargoLine, type: PalletType): CargoLine {
    if (type === "half") {
      return {
        ...line,
        type: "half",
        sizeMode: "ldm",
        length: "",
        width: "",
        height: "",
        ldm: "",
      };
    }
    if (type === "unspecified") {
      return { ...line, type, sizeMode: "ldm", length: "", width: "" };
    }
    if (type === "custom") return { ...line, type, sizeMode: "dimensions" };
    const fp = PALLET_FOOTPRINTS[type];
    return { ...line, type, sizeMode: "dimensions", length: String(fp.length), width: String(fp.width) };
  }

  applyLdm(line: CargoLine, ldm: string): CargoLine {
    if (line.type === "half") return line;
    return { ...line, ldm: LdmValue.capInput(ldm, LIMITS.ldm.max) };
  }

  applyLength(line: CargoLine, length: string): CargoLine {
    return { ...line, length: BoundedInt.capInput(length, LIMITS.lengthCm.min, LIMITS.lengthCm.max) };
  }

  applyWidth(line: CargoLine, width: string): CargoLine {
    return { ...line, width: BoundedInt.capInput(width, LIMITS.widthCm.min, LIMITS.widthCm.max) };
  }

  applyHeight(line: CargoLine, height: string): CargoLine {
    return { ...line, height: BoundedInt.capInput(height, LIMITS.heightCm.min, LIMITS.heightCm.max) };
  }

  applySizeMode(line: CargoLine, sizeMode: CargoSizeMode): CargoLine {
    if (line.type === "half") return this.applyType(line, "half");
    if (sizeMode === "ldm" || line.type === "unspecified") {
      return { ...line, type: "unspecified", sizeMode: "ldm", length: "", width: "" };
    }
    return { ...line, sizeMode };
  }

  static isSizeLocked(type: PalletType): boolean {
    return type === "half";
  }

  static isLdmOnlyType(type: PalletType): boolean {
    return type === "unspecified";
  }

  static isSizeMode(value: string): value is CargoSizeMode {
    return value === "dimensions" || value === "ldm";
  }

  static isPalletType(value: string): value is PalletType {
    return value in PALLET_FOOTPRINTS || value === "custom" || value === "unspecified" || value === "half";
  }
}
